<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import TrackPointsTable from '@/components/TrackPointsTable.vue'

/**
 * Fastest lap standings: the points taken on every track from the lap times, summed up.
 *
 * The rows come from the server (see LeaderboardController::summary): the same table feeds the
 * record titles, and a second copy built here would drift from it. The table itself is the shared
 * one (see TrackPointsTable); this component only cuts the rows it shows.
 */
const props = defineProps({
  /**
   * Standings built server-side by App\Libraries\FastestLapLib::standings():
   * { pointsPerPosition, trackKeys, rows }.
   */
  standings: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const { t } = useI18n()

/** Rows the card shows: the standings go on, this is the top of them. */
const MAX_ROWS = 10

const rows = computed(() => (props.standings?.rows ?? []).slice(0, MAX_ROWS))
</script>

<template>
  <v-card elevation="3" rounded="lg" class="mb-6 overflow-hidden">
    <!-- Header -->
    <div class="standings-header d-flex align-center ga-3 pa-4">
      <v-avatar color="amber-darken-2" variant="tonal" size="48">
        <v-icon size="28">mdi-trophy-variant</v-icon>
      </v-avatar>
      <div class="flex-grow-1">
        <h2 class="text-h6 font-weight-bold">{{ t('leaderboard.standings') }}</h2>
        <p class="text-caption text-medium-emphasis mb-0">{{ t('leaderboard.standingsSubtitle') }}</p>
      </div>
    </div>

    <v-divider />

    <!-- Loading -->
    <div v-if="loading" class="d-flex align-center justify-center ga-3 pa-8 text-medium-emphasis">
      <v-progress-circular indeterminate size="24" width="2" />
      <span>{{ t('leaderboard.loading') }}</span>
    </div>

    <!-- Empty -->
    <div v-else-if="!rows.length" class="text-center pa-8">
      <v-icon size="56" color="medium-emphasis" class="mb-3">mdi-trophy-outline</v-icon>
      <p class="text-medium-emphasis mb-0">{{ t('leaderboard.noStandingsYet') }}</p>
    </div>

    <!-- One column per track, and the total: the table is the shared one (see TrackPointsTable), so
         the standings of a season next to it look exactly the same. -->
    <TrackPointsTable
      v-else
      :rows="rows"
      :track-keys="standings.trackKeys"
      :points-per-position="standings.pointsPerPosition"
    />
  </v-card>
</template>

<style scoped>
.standings-header {
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.12), rgba(var(--v-theme-secondary), 0.12));
}
</style>
